import type { IOnlineViewer } from '@upa/design-system';

/**
 * Resolves the "Online Durchblättern" link of a product into something the
 * frontend can actually render.
 *
 * Both providers block framing of their normal document pages via
 * X-Frame-Options, but they signal an embeddable document differently:
 *
 * - Issuu encodes it in an opaque query token (`?fr=`, `?e=`, `?mode=`) that
 *   only Issuu can mint, so such a url can be recognised but never repaired.
 * - Yumpu encodes it in the path (`/embed/view/<id>`), so a share url like
 *   `/de/document/read/<id>/<slug>` can be rewritten into an embeddable one.
 * - Heyzine (a third provider already present in the catalogue) serves its
 *   flip-books framable as they are.
 *
 * Anything we cannot embed is returned as `external` so it opens in a new tab
 * instead of rendering the browser's broken-frame placeholder inside a modal.
 * An X-Frame-Options block cannot be detected from JavaScript, so the decision
 * has to be made here, before the iframe is created.
 */

const ISSUU_HOST = /(^|\.)issuu\.com$/;
const YUMPU_HOST = /(^|\.)yumpu\.com$/;
const HEYZINE_HOST = /(^|\.)heyzine\.com$/;

/** Query params that mark an Issuu url as an embeddable reader. */
const ISSUU_EMBED_PARAMS = ['fr', 'e', 'mode'];

/** `/de/embed/view/71072220` – already embeddable, locale segment optional. */
const YUMPU_EMBED_PATH = /^\/(?:([a-z]{2})\/)?embed\/view\/(\d+)/;

/** `/de/document/read/71072220/sommermagazin-2026` – rewritable to an embed url. */
const YUMPU_DOCUMENT_PATH = /^\/(?:([a-z]{2})\/)?document\/(?:read|view)\/(\d+)/;

const DEFAULT_YUMPU_LOCALE = 'de';

/** Heyzine flip-books are served without X-Frame-Options and frame as they are. */
const HEYZINE_EMBED_PATH = /^\/flip-book\//;

const parse = (raw?: string | null): URL | null => {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  try {
    return new URL(trimmed);
  } catch {
    return null;
  }
};

const resolveYumpu = (url: URL): IOnlineViewer => {
  const embed = YUMPU_EMBED_PATH.exec(url.pathname);
  if (embed) {
    return { mode: 'embed', url: url.toString(), provider: 'yumpu' };
  }

  const document = YUMPU_DOCUMENT_PATH.exec(url.pathname);
  if (document) {
    const [, locale = DEFAULT_YUMPU_LOCALE, id] = document;
    return {
      mode: 'embed',
      url: `https://www.yumpu.com/${locale}/embed/view/${id}`,
      provider: 'yumpu',
    };
  }

  return { mode: 'external', url: url.toString(), provider: 'yumpu' };
};

const resolveIssuu = (url: URL): IOnlineViewer => {
  // The reader token cannot be derived, so an url without one stays external.
  const isEmbeddable = ISSUU_EMBED_PARAMS.some((param) =>
    url.searchParams.has(param),
  );

  return {
    mode: isEmbeddable ? 'embed' : 'external',
    url: url.toString(),
    provider: 'issuu',
  };
};

/**
 * Turns a single stored url into a viewer target, or null when there is
 * nothing usable to link to.
 */
export function resolveViewerUrl(raw?: string | null): IOnlineViewer | null {
  const url = parse(raw);
  if (!url) return null;

  if (YUMPU_HOST.test(url.hostname)) return resolveYumpu(url);
  if (ISSUU_HOST.test(url.hostname)) return resolveIssuu(url);
  if (HEYZINE_HOST.test(url.hostname)) {
    return {
      mode: HEYZINE_EMBED_PATH.test(url.pathname) ? 'embed' : 'external',
      url: url.toString(),
      provider: 'heyzine',
    };
  }

  return { mode: 'external', url: url.toString(), provider: 'unknown' };
}
